<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from "vue"

const props = defineProps({
  title: { type: String, default: "Report" },
  showTitle: { type: Boolean, default: false },
  actionLabel: { type: String, default: "View Report" },
  columns: { type: Number, default: 1 },
  fields: { type: Array, default: () => [] },
  stackAt: { type: String, default: "never" },
  contentWidth: { type: String, default: "full" },
  buttonClass: { type: String, default: "" },
  modelValue: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
  errors: { type: Object, default: () => ({}) },
})

const emit = defineEmits(["update:modelValue", "submit"])

function safeClone(val) {
  try {
    if (typeof structuredClone === "function") return structuredClone(val)
  } catch {}
  try {
    return JSON.parse(JSON.stringify(val ?? {}))
  } catch {
    return Array.isArray(val) ? [...val] : { ...(val || {}) }
  }
}

const form = ref(safeClone(props.modelValue))
let syncingFromProps = false

watch(
  () => props.modelValue,
  async (v) => {
    syncingFromProps = true
    form.value = safeClone(v ?? {})
    await nextTick()
    syncingFromProps = false
  },
  { deep: true }
)

watch(
  form,
  (v) => {
    if (syncingFromProps) return
    emit("update:modelValue", v)
  },
  { deep: true }
)

const resolvedOptions = ref({})

async function hydrateOptions() {
  await Promise.all(
    props.fields.map(async (f) => {
      if (typeof f.loader === "function") {
        try { resolvedOptions.value[f.name] = (await f.loader()) || [] }
        catch { resolvedOptions.value[f.name] = f.options || [] }
      } else {
        resolvedOptions.value[f.name] = f.options || []
      }
    })
  )
}

onMounted(hydrateOptions)

// تابع تغيير الـ options مباشرة داخل كل field
watch(
  () => props.fields.map(f => f.options),
  async () => {
    await Promise.all(
      props.fields.map(async (f) => {
        if (typeof f.loader === "function") {
          try { resolvedOptions.value[f.name] = (await f.loader()) || [] }
          catch { resolvedOptions.value[f.name] = f.options || [] }
        } else {
          resolvedOptions.value[f.name] = f.options || []
        }
      })
    )
  },
  { deep: true }
)

// ── Searchable select state ──
const openSelect = ref(null)
const searchTerms = ref({})

function toggleSelect(name) {
  if (openSelect.value === name) {
    openSelect.value = null
    searchTerms.value[name] = ""
  } else {
    openSelect.value = name
    searchTerms.value[name] = searchTerms.value[name] || ""
  }
}

function selectOption(name, opt) {
  form.value[name] = opt.value
  openSelect.value = null
  searchTerms.value[name] = ""
}

function selectedLabel(name, placeholder) {
  const value = form.value[name]
  if (value === "" || value === null || value === undefined) return placeholder || ""
  const list = resolvedOptions.value[name] || []
  const found = list.find((o) => o.value === value)
  return found ? found.label : placeholder || ""
}

function filteredOptions(name) {
  const list = resolvedOptions.value[name] || []
  const q = (searchTerms.value[name] || "").toLowerCase().trim()
  if (!q) return list
  return list.filter((opt) => String(opt.label || "").toLowerCase().includes(q))
}

function onClickOutside(e) {
  if (!e.target.closest(".rf-select-wrapper")) {
    openSelect.value = null
  }
}

onMounted(() => document.addEventListener("click", onClickOutside))
onBeforeUnmount(() => document.removeEventListener("click", onClickOutside))

// ── Layout helpers ──
function gridColsClass() {
  const c = Math.max(1, Math.min(props.columns, 4))
  if (props.stackAt === "never") return `grid-cols-${c}`
  return `grid-cols-1 ${props.stackAt}:grid-cols-${c}`
}
function spanClass(span) {
  const c = Math.max(1, Math.min(props.columns, 4))
  if (span === "full") {
    return props.stackAt === "never"
      ? `col-span-${c}`
      : `col-span-1 ${props.stackAt}:col-span-${c}`
  }
  const n = Math.max(1, Math.min(Number(span || 1), c))
  return props.stackAt === "never"
    ? `col-span-${n}`
    : `col-span-1 ${props.stackAt}:col-span-${n}`
}
function contentWidthClass() {
  const map = { sm: "max-w-sm", md: "max-w-md", lg: "max-w-lg", xl: "max-w-xl", "2xl": "max-w-2xl", full: "max-w-none" }
  return map[props.contentWidth] || "max-w-none"
}

function ringClass(name) {
  return props.errors[name]
    ? "ring-2 ring-red-400 focus:ring-red-400"
    : "ring-2 ring-gray-300 focus:ring-[#5B9AC5]"
}

function onSubmit() { emit("submit", safeClone(form.value)) }
</script>

<template>
  <div class="rounded-xl border border-gray-200 bg-white shadow-sm">
    <div class="p-6 pt-10">
      <h2 v-if="showTitle" class="text-[22px] sm:text-[24px] leading-7 font-bold text-gray-900 mb-6">{{ title }}</h2>

      <div class="mx-auto" :class="contentWidthClass()">
        <div class="grid gap-4" :class="gridColsClass()">
          <div v-for="f in props.fields" :key="f.name" :class="spanClass(f.span)">
            <template v-if="f.type === 'spacer'"></template>
            <template v-else>
              <label v-if="f.label" class="block text-sm font-medium text-gray-700 mb-1">{{ f.label }}</label>

              <!-- ── Searchable Select ── -->
              <div v-if="f.type === 'select'" class="relative rf-select-wrapper">
                <button
                  type="button"
                  class="w-full h-[47px] rounded-xl border-transparent px-3 text-sm flex items-center justify-between focus:outline-none"
                  :class="ringClass(f.name)"
                  @click.stop="toggleSelect(f.name)"
                >
                  <span class="truncate" :class="{ 'text-gray-400': form[f.name] === '' || form[f.name] === null || form[f.name] === undefined }">
                    {{ selectedLabel(f.name, f.placeholder || '') }}
                  </span>
                  <svg class="w-4 h-4 ml-2 text-gray-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 011.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/>
                  </svg>
                </button>

                <div
                  v-if="openSelect === f.name"
                  class="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
                >
                  <div class="p-2 border-b border-gray-100">
                    <input
                      type="text"
                      v-model="searchTerms[f.name]"
                      placeholder="Search..."
                      class="w-full h-9 rounded-lg border border-gray-300 px-2 text-sm focus:outline-none focus:border-[#5B9AC5]"
                      @click.stop
                    />
                  </div>
                  <ul class="max-h-60 overflow-y-auto py-1 text-sm">
                    <li
                      v-for="opt in filteredOptions(f.name)"
                      :key="String(opt.value ?? opt.label)"
                      class="px-3 py-2 cursor-pointer hover:bg-gray-100"
                      :class="{ 'bg-[#5B9AC5] text-white hover:bg-[#5B9AC5]': form[f.name] === opt.value }"
                      @click.stop="selectOption(f.name, opt)"
                    >
                      {{ opt.label ?? opt.value }}
                    </li>
                    <li v-if="filteredOptions(f.name).length === 0" class="px-3 py-2 text-xs text-gray-400">
                      No results
                    </li>
                  </ul>
                </div>

                <p v-if="errors[f.name]" class="mt-1 text-xs text-red-500">{{ errors[f.name] }}</p>
              </div>

              <!-- ── Date ── -->
              <template v-else-if="f.type === 'date'">
                <input
                  type="date"
                  v-model="form[f.name]"
                  class="w-full h-[47px] rounded-xl border-transparent px-3 text-sm focus:outline-none"
                  :class="ringClass(f.name)"
                  :placeholder="f.placeholder || 'yyyy/mm/dd'"
                />
                <p v-if="errors[f.name]" class="mt-1 text-xs text-red-500">{{ errors[f.name] }}</p>
              </template>

              <!-- ── Radio ── -->
              <template v-else-if="f.type === 'radio'">
                <div class="flex flex-wrap gap-4">
                  <label v-for="opt in (f.options || [])" :key="String(opt.value ?? opt.label)"
                         class="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input type="radio" :name="f.name" :value="opt.value ?? opt.label" v-model="form[f.name]">
                    <span>{{ opt.label ?? opt.value }}</span>
                  </label>
                </div>
                <p v-if="errors[f.name]" class="mt-1 text-xs text-red-500">{{ errors[f.name] }}</p>
              </template>

              <!-- ── Checkbox ── -->
              <template v-else-if="f.type === 'checkbox'">
                <div class="flex flex-wrap gap-4">
                  <label v-for="opt in (f.options || [])" :key="String(opt.value ?? opt.label)"
                         class="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" :value="opt.value ?? opt.label" v-model="form[f.name]">
                    <span>{{ opt.label ?? opt.value }}</span>
                  </label>
                </div>
                <p v-if="errors[f.name]" class="mt-1 text-xs text-red-500">{{ errors[f.name] }}</p>
              </template>

              <!-- ── Text ── -->
              <template v-else>
                <input
                  type="text"
                  v-model="form[f.name]"
                  class="w-full h-[47px] rounded-xl border-transparent px-3 text-sm focus:outline-none"
                  :class="ringClass(f.name)"
                  :placeholder="f.placeholder || ''"
                />
                <p v-if="errors[f.name]" class="mt-1 text-xs text-red-500">{{ errors[f.name] }}</p>
              </template>
            </template>
          </div>
        </div>
      </div>

      <div class="mt-6 mx-auto" :class="contentWidthClass()">
        <div class="flex justify-center">
          <button
            type="button"
            class="w-full h-10 px-4 mt-4 rounded-full text-white text-sm font-medium shadow-sm hover:opacity-95 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            :class="buttonClass"
            :style="{ backgroundColor: '#5B9AC5' }"
            :disabled="loading"
            @click="onSubmit"
          >
            <svg
              v-if="loading"
              class="animate-spin h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
            {{ actionLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
